import { makeAutoObservable } from 'mobx'
import { ScheduleStore } from 'shared/ui/Schedule'
import type {
  IResponseBroadcastById,
  IResponseRecurringBroadcast,
  IResponseRecurringBroadcastById,
  IResponseRecurringBroadcastStatus,
} from 'entities/Broadcast/api/types'
import type { IBroadcastActionTypes, IBroadcastType } from 'entities/Broadcast'
import { BroadcastRecurringStatistic } from 'entities/Broadcast/model/BroadcastRecurringStatistic'
import type { IEntitiesStore } from 'entities/EntitiesExport'

export class BroadcastRecurring {
  id: number
  type!: IBroadcastType
  name!: string
  owner_id!: number
  status!: IResponseRecurringBroadcastStatus
  created_at!: string
  failed_reason!: string | null
  private _statistics: BroadcastRecurringStatistic | null = null

  settings?: IResponseRecurringBroadcast['settings'] | null = null
  scheduleStore = new ScheduleStore({
    sendLaterStoreProps: {
      withAdvancedScheduling: true,
    },
  })

  constructor(
    item: IResponseRecurringBroadcast | IResponseRecurringBroadcastById,
    private _entities: IEntitiesStore
  ) {
    this.id = item.id
    this.syncOrigin(item)

    makeAutoObservable(this)
  }

  syncOrigin = (item: IResponseRecurringBroadcast | IResponseRecurringBroadcastById) => {
    this.owner_id = item.owner_id
    this.name = item.name
    this.created_at = item.created_at
    this.type =
      item.send_from?.type === 'from_rcs_sender' ? 'rcs' : item.is_rvm ? 'ringless' : 'sms'

    if ('recurring_template_settings' in item) {
      this.status = item.recurring_template_settings.status
      this.settings = item.recurring_template_settings
      this._statistics = new BroadcastRecurringStatistic(item, this._entities)
      this.failed_reason = item.failed_reason
    } else {
      this.status = item.settings.status
      this.settings = item.settings
      this.failed_reason = null
    }
    this.scheduleStore.init({
      type: 'recurring',
      isDisabledSendLater: false,
      config: {
        repeatPeriod: this.settings.type,
        customInterval: this.settings.send_settings?.interval,
        customPeriod: this.settings.send_settings?.type,
        date: this.created_at,
        endRepeat: this.settings?.duration_settings?.type,
        endDate: this.settings?.duration_settings?.date,
        endTimes: this.settings?.duration_settings?.occurrences,
        isUseContactTimezone: false,
        advancedDate: {
          days: this?.settings.send_settings?.schedule.days,
          isAdvancedScheduling: !!this?.settings.send_settings?.schedule?.days.length,
          time: '9:00',
        },
        isDisabledRepeat: false,
        isDisabledEnd: false,
        monthDaysSelectorStoreProps: {
          type: this.settings.send_settings?.recurring_month_is_calendar ? 'each' : 'onThe',
          days: this.settings.send_settings?.recurring_dates,
          onTheDayOfWeek: this.settings.send_settings?.recurring_month_nth_day,
          onTheOrdinal: this.settings.send_settings?.recurring_month_nth_type,
        },
      },
    })
  }

  get statistics() {
    return this._statistics
  }

  get actionTypes(): IBroadcastActionTypes[] {
    return ['edit']
  }

  get user() {
    const userFormStore = this._entities.usersStore.getItem(this.owner_id)

    return userFormStore || this._entities.usersStore.getItem(this.owner_id, true)
  }

  handleUpdate = (response: IResponseBroadcastById) => {
    if (response.status === 'recurring_template') {
      this.syncOrigin(response)
    }
  }

  get recurringTitle() {
    return this.scheduleStore.repeatText
  }

  get isInvalid() {
    return this.status === 'invalid'
  }
}
